import React, { useState, useEffect } from 'react';
import { FileUpload } from './FileUpload';
import { Button } from './Button';
import { TokenTracker } from './TokenTracker';
import { generateVirtualTryOn, generateVeoVideo, ensureApiKeySelected } from './geminiService';
import { fileToBase64, readFileAsDataURL, urlToBase64 } from './utils';
import { GenerationResult, UsageStats } from './types';

// Template Data
const PERSON_TEMPLATES = [
  { id: 'p1', url: 'https://thumbs.dreamstime.com/b/full-body-portrait-handsome-young-man-smiling-african-american-isolated-white-background-46309994.jpg', label: 'Model 1' },
  { id: 'p3', url: 'https://thumbs.dreamstime.com/b/full-body-confident-happy-woman-standing-against-isolated-white-background-portrait-120172352.jpg', label: 'Model 3' },
  { id: 'p4', url: 'https://st4.depositphotos.com/1715570/19780/i/1600/depositphotos_197801828-stock-photo-full-body-portrait-fashionable-young.jpg', label: 'Model 4' }
];

const GARMENT_TEMPLATES = [
  { id: 'g1', url: 'https://colorfulstandard.com/cdn/shop/files/CS2056_Male_OversizedOrganicT-Shirt-DeepBlack_2_2adc696d-0930-4a7f-86b1-61ad0c6dc3e9.jpg?v=1745834597&width=3000', label: 'Black Tee' },
  { id: 'g2', url: 'https://pangaia.com/cdn/shop/files/DNA_Oversized_T-Shirt_-Summit_Blue-1.png?crop=center&height=1999&v=1755260238&width=1500', label: 'Blue Tee' },
  { id: 'g3', url: 'https://shop.staplescopyandprint.ca/cdn/shop/files/StaplesPrint_720x720_Holiday_PDP_ProductCards_CustomTShirt.png?v=1762202817', label: 'White Tee' }
];

export const OutfitVirtualTryOn: React.FC = () => {
  // Mode State
  const [isComparisonMode, setIsComparisonMode] = useState(false);

  // Stats State
  const [usageStats, setUsageStats] = useState<UsageStats>({
    promptTokenCount: 0,
    candidatesTokenCount: 0,
    totalTokenCount: 0,
    imagesGenerated: 0,
    videosGenerated: 0,
  });
  const [lastRunStats, setLastRunStats] = useState<UsageStats | null>(null);

  // Person State
  const [personFile, setPersonFile] = useState<File | null>(null);
  const [personUrl, setPersonUrl] = useState<string | null>(null);
  const [personPreview, setPersonPreview] = useState<string | null>(null);
  
  // Garment 1 State
  const [garmentFile, setGarmentFile] = useState<File | null>(null);
  const [garmentUrl, setGarmentUrl] = useState<string | null>(null);
  const [garmentPreview, setGarmentPreview] = useState<string | null>(null);

  // Garment 2 State (Comparison)
  const [garment2File, setGarment2File] = useState<File | null>(null);
  const [garment2Url, setGarment2Url] = useState<string | null>(null);
  const [garment2Preview, setGarment2Preview] = useState<string | null>(null);
  
  // Results
  const [resultImage, setResultImage] = useState<string | null>(null);
  const [resultImage2, setResultImage2] = useState<string | null>(null);
  
  // Status
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Veo State
  const [activeVeoImage, setActiveVeoImage] = useState<string | null>(null); // Which result is selected for video
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);

  // Initialize from URL params or defaults
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const modelParam = params.get('model');
    const garmentParam = params.get('garment');
    
    // Handle Model Init
    if (modelParam) {
        setPersonUrl(modelParam);
        setPersonPreview(modelParam);
    } else if (!personUrl && !personFile) {
        setPersonUrl(PERSON_TEMPLATES[0].url);
        setPersonPreview(PERSON_TEMPLATES[0].url);
    }

    // Handle Garment Init
    if (garmentParam) {
        setGarmentUrl(garmentParam);
        setGarmentPreview(garmentParam);
    } else if (!garmentUrl && !garmentFile) {
        setGarmentUrl(GARMENT_TEMPLATES[1].url);
        setGarmentPreview(GARMENT_TEMPLATES[1].url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for Chrome Extension Messages (Data Passing)
  useEffect(() => {
    const handleExtensionMessage = (event: MessageEvent) => {
      // Security check: ensure message is from our own window (relayed by content script)
      if (event.source !== window) return;
      
      const { type, payload } = event.data;

      if (type === 'EXTENSION_SET_MODEL') {
        console.log("Received Model from Extension");
        setPersonFile(null);
        setPersonUrl(payload); // payload is a Data URL (base64), which our utils handle fine
        setPersonPreview(payload);
      } 
      else if (type === 'EXTENSION_SET_GARMENT') {
        console.log("Received Garment from Extension");
        setGarmentFile(null);
        setGarmentUrl(payload); // payload is a Data URL
        setGarmentPreview(payload);
      }
    };

    window.addEventListener('message', handleExtensionMessage);
    return () => window.removeEventListener('message', handleExtensionMessage);
  }, []);

  // Update active veo image when results change
  useEffect(() => {
    if (resultImage && !resultImage2) setActiveVeoImage(resultImage);
    else if (resultImage && resultImage2 && !activeVeoImage) setActiveVeoImage(resultImage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultImage, resultImage2]);

  // Helper to accumulate stats securely
  const accumulateStats = (runStats: UsageStats) => {
    setLastRunStats(runStats);
    setUsageStats(prev => ({
      promptTokenCount: prev.promptTokenCount + runStats.promptTokenCount,
      candidatesTokenCount: prev.candidatesTokenCount + runStats.candidatesTokenCount,
      totalTokenCount: prev.totalTokenCount + runStats.totalTokenCount,
      imagesGenerated: prev.imagesGenerated + runStats.imagesGenerated,
      videosGenerated: prev.videosGenerated + runStats.videosGenerated,
    }));
  };

  const handlePersonSelect = async (file: File) => {
    setPersonFile(file);
    setPersonUrl(null);
    setPersonPreview(await readFileAsDataURL(file));
  };

  const handlePersonTemplateClick = (url: string) => {
    setPersonFile(null);
    setPersonUrl(url);
    setPersonPreview(url);
  };

  const handleGarmentSelect = async (file: File, slot: 1 | 2 = 1) => {
    const preview = await readFileAsDataURL(file);
    if (slot === 1) {
      setGarmentFile(file);
      setGarmentUrl(null);
      setGarmentPreview(preview);
    } else {
      setGarment2File(file);
      setGarment2Url(null);
      setGarment2Preview(preview);
    }
  };

  const handleGarmentTemplateClick = (url: string, slot: 1 | 2 = 1) => {
    if (slot === 1) {
      setGarmentFile(null);
      setGarmentUrl(url);
      setGarmentPreview(url);
    } else {
      setGarment2File(null);
      setGarment2Url(url);
      setGarment2Preview(url);
    }
  };

  const handleGenerate = async () => {
    // Basic validation
    if (!personFile && !personUrl) return;
    if (!garmentFile && !garmentUrl) return;
    if (isComparisonMode && (!garment2File && !garment2Url)) return;

    setIsLoading(true);
    setError(null);
    setResultImage(null);
    setResultImage2(null);
    setVideoUrl(null);
    setVideoError(null);
    setActiveVeoImage(null);

    // Scroll to top on mobile
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      // Prepare Person Base64
      let personBase64: string;
      if (personFile) personBase64 = await fileToBase64(personFile);
      else if (personUrl) personBase64 = await urlToBase64(personUrl);
      else throw new Error("Missing person image");

      // Prepare Garment 1 Base64
      let garmentBase64: string;
      if (garmentFile) garmentBase64 = await fileToBase64(garmentFile);
      else if (garmentUrl) garmentBase64 = await urlToBase64(garmentUrl);
      else throw new Error("Missing garment image");

      // Request 1
      const request1 = generateVirtualTryOn(personBase64, garmentBase64);
      
      let request2: Promise<GenerationResult> | null = null;

      // Prepare Garment 2 Base64 if needed
      if (isComparisonMode) {
        let garment2Base64: string;
        if (garment2File) garment2Base64 = await fileToBase64(garment2File);
        else if (garment2Url) garment2Base64 = await urlToBase64(garment2Url);
        else throw new Error("Missing garment 2 image");
        
        request2 = generateVirtualTryOn(personBase64, garment2Base64);
      }

      // Track usage for this specific run
      const currentRunStats: UsageStats = {
          promptTokenCount: 0,
          candidatesTokenCount: 0,
          totalTokenCount: 0,
          imagesGenerated: 0,
          videosGenerated: 0
      };

      // Execute
      if (request2) {
        const [res1, res2] = await Promise.all([request1, request2]);
        setResultImage(res1.mediaData);
        setResultImage2(res2.mediaData);
        setActiveVeoImage(res1.mediaData);
        
        // Sum up stats
        if (res1.usage) {
            currentRunStats.promptTokenCount += res1.usage.promptTokenCount || 0;
            currentRunStats.candidatesTokenCount += res1.usage.candidatesTokenCount || 0;
            currentRunStats.totalTokenCount += res1.usage.totalTokenCount || 0;
            currentRunStats.imagesGenerated += 1;
        }
        if (res2.usage) {
            currentRunStats.promptTokenCount += res2.usage.promptTokenCount || 0;
            currentRunStats.candidatesTokenCount += res2.usage.candidatesTokenCount || 0;
            currentRunStats.totalTokenCount += res2.usage.totalTokenCount || 0;
            currentRunStats.imagesGenerated += 1;
        }

      } else {
        const res1 = await request1;
        setResultImage(res1.mediaData);
        setActiveVeoImage(res1.mediaData);

        if (res1.usage) {
            currentRunStats.promptTokenCount += res1.usage.promptTokenCount || 0;
            currentRunStats.candidatesTokenCount += res1.usage.candidatesTokenCount || 0;
            currentRunStats.totalTokenCount += res1.usage.totalTokenCount || 0;
            currentRunStats.imagesGenerated += 1;
        }
      }

      // Update both Total and Last Run
      accumulateStats(currentRunStats);

    } catch (err: any) {
      setError(err.message || "Failed to generate try-on image");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateVideo = async () => {
    if (!activeVeoImage) return;

    setIsVideoLoading(true);
    setVideoError(null);
    
    try {
        await ensureApiKeySelected();
        
        const [meta, base64] = activeVeoImage.split(',');
        const mimeType = meta.split(':')[1].split(';')[0];
        
        const prompt = "A fashion model walking a few steps to the left and then to the right, showing off the outfit, full body, photorealistic, 4k.";
        
        const url = await generateVeoVideo(base64, prompt, '9:16', mimeType);
        
        setVideoUrl(url);
        
        // Update Video Stats
        const videoRunStats: UsageStats = {
            promptTokenCount: 0,
            candidatesTokenCount: 0,
            totalTokenCount: 0,
            imagesGenerated: 0,
            videosGenerated: 1
        };
        accumulateStats(videoRunStats);

    } catch (err: any) {
        console.error("Veo Error", err);
        let msg = err.message || "Failed to generate video";
        if (msg.includes("Requested entity was not found")) {
            msg = "API Key session expired. Please re-select your key.";
            if (window.aistudio && window.aistudio.openSelectKey) {
                window.aistudio.openSelectKey().catch(console.error);
            }
        }
        setVideoError(msg);
    } finally {
        setIsVideoLoading(false);
    }
  };

  return (
    <div className="w-full max-w-[1400px] mx-auto pb-12">
      <TokenTracker stats={usageStats} lastRunStats={lastRunStats} />
      
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 h-full">
        
        {/* Left Column: Configuration Controls */}
        <div className="lg:col-span-4 space-y-8 lg:sticky lg:top-24 lg:h-[calc(100vh-8rem)] lg:overflow-y-auto pr-2 custom-scrollbar">
          
          {/* Section 1: Model Selection */}
          <div className="glass-panel p-6 rounded-2xl space-y-6 bg-slate-800/50 border border-slate-700">
             <div className="flex items-center space-x-2">
                <div className="w-1 h-5 bg-violet-500 rounded-full"></div>
                <h3 className="text-lg font-bold text-white tracking-wide">1. Select Model</h3>
             </div>
             
             <div className="grid grid-cols-4 gap-3">
                {PERSON_TEMPLATES.map((t) => (
                  <button 
                    key={t.id}
                    onClick={() => handlePersonTemplateClick(t.url)}
                    className={`relative aspect-[3/4] rounded-lg overflow-hidden transition-all duration-300 group
                      ${personUrl === t.url ? 'ring-2 ring-violet-500 shadow-[0_0_15px_-3px_rgba(139,92,246,0.5)]' : 'ring-1 ring-white/10 opacity-70 hover:opacity-100 hover:ring-white/30'}
                    `}
                    title={t.label}
                  >
                    <img src={t.url} alt={t.label} className="w-full h-full object-cover" />
                    {personUrl === t.url && (
                        <div className="absolute inset-0 bg-violet-500/20 backdrop-blur-[1px]"></div>
                    )}
                  </button>
                ))}
             </div>
             
             <div className="pt-2">
                 <FileUpload 
                  label="OR UPLOAD YOUR OWN" 
                  onFileSelect={handlePersonSelect} 
                  currentPreview={personFile ? personPreview : null}
                 />
             </div>
          </div>

          {/* Section 2: Garment Selection */}
          <div className="glass-panel p-6 rounded-2xl space-y-6 bg-slate-800/50 border border-slate-700">
             <div className="flex items-center justify-between">
               <div className="flex items-center space-x-2">
                  <div className="w-1 h-5 bg-indigo-500 rounded-full"></div>
                  <h3 className="text-lg font-bold text-white tracking-wide">2. Select Garment</h3>
               </div>
               
               {/* Comparison Toggle */}
               <div className="flex items-center space-x-2">
                  <span className={`text-xs font-semibold uppercase tracking-wider ${isComparisonMode ? 'text-indigo-400' : 'text-gray-500'}`}>Compare</span>
                  <button 
                    onClick={() => setIsComparisonMode(!isComparisonMode)}
                    className={`w-12 h-6 rounded-full p-1 transition-colors duration-300 ${isComparisonMode ? 'bg-indigo-600' : 'bg-gray-700'}`}
                  >
                    <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-300 ${isComparisonMode ? 'translate-x-6' : 'translate-x-0'}`} />
                  </button>
               </div>
             </div>
             
             {/* Garment A Controls */}
             <div>
               <div className="text-xs font-semibold text-indigo-300 mb-3 uppercase tracking-wider flex items-center justify-between">
                 <span>Garment A (Primary)</span>
                 {isComparisonMode && <span className="text-xs bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded">Look 1</span>}
               </div>
               <div className="grid grid-cols-4 gap-3 mb-4">
                  {GARMENT_TEMPLATES.map((t) => (
                    <button 
                      key={t.id}
                      onClick={() => handleGarmentTemplateClick(t.url, 1)}
                      className={`relative aspect-square rounded-lg overflow-hidden transition-all duration-300 group
                        ${garmentUrl === t.url ? 'ring-2 ring-indigo-500 shadow-[0_0_15px_-3px_rgba(99,102,241,0.5)]' : 'ring-1 ring-white/10 opacity-70 hover:opacity-100 hover:ring-white/30'}
                      `}
                      title={t.label}
                    >
                      <img src={t.url} alt={t.label} className="w-full h-full object-cover" />
                      {garmentUrl === t.url && (
                          <div className="absolute inset-0 bg-indigo-500/20 backdrop-blur-[1px]"></div>
                      )}
                    </button>
                  ))}
               </div>
               <FileUpload 
                  label="" 
                  onFileSelect={(f) => handleGarmentSelect(f, 1)} 
                  currentPreview={garmentFile ? garmentPreview : null}
               />
             </div>

             {/* Garment B Controls (Conditional) */}
             {isComparisonMode && (
               <div className="pt-6 border-t border-white/10 animate-in slide-in-from-top-4 duration-300">
                  <div className="text-xs font-semibold text-pink-300 mb-3 uppercase tracking-wider flex items-center justify-between">
                    <span>Garment B (Comparison)</span>
                    <span className="text-xs bg-pink-500/20 text-pink-300 px-2 py-0.5 rounded">Look 2</span>
                  </div>
                  <div className="grid grid-cols-4 gap-3 mb-4">
                      {GARMENT_TEMPLATES.map((t) => (
                        <button 
                          key={t.id}
                          onClick={() => handleGarmentTemplateClick(t.url, 2)}
                          className={`relative aspect-square rounded-lg overflow-hidden transition-all duration-300 group
                            ${garment2Url === t.url ? 'ring-2 ring-pink-500 shadow-[0_0_15px_-3px_rgba(236,72,153,0.5)]' : 'ring-1 ring-white/10 opacity-70 hover:opacity-100 hover:ring-white/30'}
                          `}
                          title={t.label}
                        >
                          <img src={t.url} alt={t.label} className="w-full h-full object-cover" />
                          {garment2Url === t.url && (
                              <div className="absolute inset-0 bg-pink-500/20 backdrop-blur-[1px]"></div>
                          )}
                        </button>
                      ))}
                  </div>
                  <FileUpload 
                      label="" 
                      onFileSelect={(f) => handleGarmentSelect(f, 2)} 
                      currentPreview={garment2File ? garment2Preview : null}
                  />
               </div>
             )}
          </div>

          <Button 
            onClick={handleGenerate} 
            isLoading={isLoading} 
            disabled={(!personFile && !personUrl) || (!garmentFile && !garmentUrl) || (isComparisonMode && !garment2File && !garment2Url)}
            className="w-full py-4 text-lg shadow-2xl"
            icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="M16.19 2H7.81C4.17 2 2 4.17 2 7.81v8.37C2 19.83 4.17 22 7.81 22h8.37c3.64 0 5.81-2.17 5.81-5.81V7.81C22 4.17 19.83 2 16.19 2zm-4.8 15.65c-.21.21-.49.32-.76.32s-.55-.11-.76-.32l-3.32-3.32a1.08 1.08 0 0 1 0-1.52c.42-.42 1.1-.42 1.52 0l1.49 1.49V8.69c0-.6.49-1.08 1.08-1.08s1.08.49 1.08 1.08v5.52l1.39-1.39c.42-.42 1.1-.42 1.52 0 .42.42.42 1.1 0 1.52l-3.24 3.31z"/></svg>
            }
          >
            {isComparisonMode ? 'Generate Comparison' : 'Generate Outfit'}
          </Button>

           {error && (
            <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-xl text-red-300 text-sm animate-pulse">
              <span className="font-bold">Error:</span> {error}
            </div>
          )}
        </div>

        {/* Right Column: Results & Video */}
        <div className="lg:col-span-8 space-y-6">
            
            {/* Result Stage */}
            <div className={`relative w-full rounded-2xl overflow-hidden transition-all duration-500 border border-white/5 ${resultImage ? 'bg-black shadow-[0_0_100px_-20px_rgba(76,29,149,0.3)]' : 'bg-gray-900/50 min-h-[600px] flex items-center justify-center border-dashed border-2 border-gray-700'}`}>
                {resultImage ? (
                    <div className="w-full h-full p-4">
                        {/* Comparison Grid */}
                        <div className={`grid gap-4 h-full ${resultImage2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                          
                          {/* Result 1 */}
                          <div className="relative group flex flex-col h-full">
                             {resultImage2 && <div className="absolute top-4 left-4 z-10 bg-indigo-600/90 text-white text-xs font-bold px-3 py-1 rounded-full backdrop-blur-md shadow-lg">LOOK 1</div>}
                             <div className="relative flex-1 bg-gray-900 rounded-xl overflow-hidden border border-white/5">
                                <img src={resultImage} alt="Result 1" className="w-full h-full object-contain" />
                             </div>
                             {!resultImage2 && (
                                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Button variant="secondary" className="!py-2 !px-4 text-xs" onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = resultImage;
                                      link.download = 'vision-outfit-result.png';
                                      link.click();
                                  }}>Download</Button>
                                </div>
                             )}
                          </div>

                          {/* Result 2 */}
                          {resultImage2 && (
                            <div className="relative group flex flex-col h-full">
                              <div className="absolute top-4 left-4 z-10 bg-pink-600/90 text-white text-xs font-bold px-3 py-1 rounded-full backdrop-blur-md shadow-lg">LOOK 2</div>
                              <div className="relative flex-1 bg-gray-900 rounded-xl overflow-hidden border border-white/5">
                                <img src={resultImage2} alt="Result 2" className="w-full h-full object-contain" />
                              </div>
                            </div>
                          )}
                        </div>
                    </div>
                ) : (
                    <div className="text-center p-8 max-w-sm mx-auto">
                        {isLoading ? (
                            <div className="flex flex-col items-center">
                                <div className="relative w-24 h-24 mb-8">
                                    <div className="absolute inset-0 rounded-full border-2 border-violet-500/20"></div>
                                    <div className="absolute inset-0 rounded-full border-t-2 border-violet-500 animate-spin"></div>
                                    <div className="absolute inset-4 rounded-full border-t-2 border-indigo-400 animate-spin" style={{ animationDuration: '1.5s' }}></div>
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-2">Designing Look...</h3>
                                <p className="text-gray-400">Our AI is analyzing fabric physics, lighting, and body pose to create your perfect fit.</p>
                            </div>
                        ) : (
                            <>
                                <div className="w-24 h-24 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-6 ring-1 ring-white/10">
                                    <svg className="w-10 h-10 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <h2 className="text-xl font-semibold text-white mb-2">Ready to Create</h2>
                                <p className="text-gray-500">Select a model and garment from the left panel to generate your high-fidelity virtual try-on.</p>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Veo Integration */}
            {resultImage && (
                <div className="p-1 rounded-2xl overflow-hidden bg-slate-800/50 border border-slate-700">
                    <div className="bg-gradient-to-br from-emerald-950/50 to-teal-950/30 p-8 rounded-xl flex flex-col lg:flex-row gap-8 relative overflow-hidden">
                        
                        {/* Decorative background glow */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                        
                        <div className="flex-1 space-y-4 z-10 text-center lg:text-left">
                            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-widest">
                                <span className="relative flex h-2 w-2">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                </span>
                                <span>Veo Cinematic</span>
                            </div>
                            <h3 className="text-2xl font-bold text-white">Bring it to the Runway</h3>
                            <p className="text-gray-400 max-w-md">Generate a photorealistic 4K video of your model walking in this outfit. Experience the drape and movement in motion.</p>
                            
                            {/* Selection for Veo if Comparison Mode */}
                            {resultImage2 && (
                                <div className="bg-emerald-900/30 p-3 rounded-lg border border-emerald-500/20 inline-block text-left">
                                    <p className="text-emerald-300 text-xs font-bold uppercase tracking-wide mb-2">Select Look to Animate</p>
                                    <div className="flex space-x-3">
                                        <button 
                                            onClick={() => { setActiveVeoImage(resultImage); setVideoUrl(null); }}
                                            className={`flex items-center space-x-2 px-3 py-2 rounded-md transition-all ${activeVeoImage === resultImage ? 'bg-emerald-600 text-white shadow-lg' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
                                        >
                                            <div className="w-2 h-2 rounded-full bg-indigo-400"></div>
                                            <span className="text-xs font-semibold">Look 1</span>
                                        </button>
                                        <button 
                                            onClick={() => { setActiveVeoImage(resultImage2); setVideoUrl(null); }}
                                            className={`flex items-center space-x-2 px-3 py-2 rounded-md transition-all ${activeVeoImage === resultImage2 ? 'bg-emerald-600 text-white shadow-lg' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
                                        >
                                            <div className="w-2 h-2 rounded-full bg-pink-400"></div>
                                            <span className="text-xs font-semibold">Look 2</span>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {!videoUrl && !isVideoLoading && (
                                <div className="flex flex-col items-center lg:items-start space-y-3">
                                    <Button 
                                        onClick={handleGenerateVideo}
                                        className="bg-gradient-to-r from-emerald-600 to-teal-600 shadow-[0_0_20px_-5px_rgba(16,185,129,0.4)] border-emerald-500/20"
                                    >
                                        Generate Runway Video
                                    </Button>
                                    <p className="text-xs text-emerald-500/60 max-w-xs">
                                        * Requires a paid API key with Google Cloud billing. 
                                        <a href="https://ai.google.dev/gemini-api/docs/billing" target="_blank" rel="noreferrer" className="underline hover:text-emerald-400 ml-1 transition-colors">
                                            Learn more
                                        </a>
                                    </p>
                                </div>
                            )}
                            
                            {videoError && (
                                <p className="text-red-400 text-sm bg-red-950/50 p-3 rounded-lg border border-red-900">{videoError}</p>
                            )}
                        </div>

                        {/* Video Player or Placeholder */}
                        <div className="flex-1 w-full max-w-sm lg:max-w-md z-10 flex justify-center">
                            {videoUrl ? (
                                <div className="relative rounded-xl overflow-hidden shadow-2xl ring-1 ring-emerald-500/30 group w-full aspect-[9/16] max-h-[400px]">
                                    <video src={videoUrl} autoPlay loop controls className="w-full h-full object-cover" />
                                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Button variant="secondary" className="!p-2 !rounded-full bg-black/50 backdrop-blur" onClick={() => {
                                            const link = document.createElement('a');
                                            link.href = videoUrl;
                                            link.download = 'vision-outfit-runway.mp4';
                                            link.click();
                                        }}>
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                                        </Button>
                                    </div>
                                </div>
                            ) : isVideoLoading ? (
                                <div className="w-full aspect-video lg:aspect-[9/16] max-h-[300px] bg-black/40 rounded-xl flex flex-col items-center justify-center border border-emerald-500/10">
                                    <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                                    <p className="text-emerald-400 font-medium animate-pulse">Rendering 4K Video...</p>
                                </div>
                            ) : (
                                <div className="w-full aspect-video lg:aspect-[9/16] max-h-[300px] bg-emerald-900/10 rounded-xl border border-emerald-500/5 flex items-center justify-center relative">
                                    {resultImage2 && activeVeoImage ? (
                                        <img src={activeVeoImage} className="w-full h-full object-cover opacity-50 blur-sm absolute inset-0" alt="background preview" />
                                    ) : null}
                                    <div className="relative z-10 flex flex-col items-center">
                                        <svg className="w-16 h-16 text-emerald-500 opacity-80 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        <span className="text-emerald-200/50 text-xs uppercase tracking-widest font-bold">Video Preview</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default OutfitVirtualTryOn;

